/**
 * SRS / spaced repetition (Duolingo-style) — PURE and DERIVED scheduler.
 *
 * It introduces no new state: the review calendar is DERIVED from the already
 * synced fields of `lesson_progress` (`attempts`, `score`, `completed_at`,
 * `updated_at`). Reviewing a lesson IS replaying it with the normal player —
 * the existing write-path increments `attempts`, re-grades `score` on the
 * server and refreshes `updated_at`, so the calendar advances on its own.
 * Zero migrations, zero new write-path, offline-first for free.
 *
 * Interval ladder (SM-2 lite): 1 → 3 → 7 → 14 → 30 → 60 days depending on the
 * number of passes (`attempts`), moderated by the quality of the last grade:
 *   - score >= STRONG_SCORE → full ladder interval.
 *   - score < STRONG_SCORE  → half interval (minimum 1 day): weak passes come back sooner.
 * Only COMPLETED lessons enter (a failed lesson is still the "current" node
 * of the path, not a review).
 */

/** Interval ladder in days by number of passes (attempts 1..N). */
export const SRS_INTERVAL_LADDER_DAYS = [1, 3, 7, 14, 30, 60] as const;

/** Grade threshold above which the last pass is considered solid. */
export const SRS_STRONG_SCORE = 0.85;

const DAY_MS = 24 * 60 * 60 * 1000;

/** Deterministic order: by due date asc, stable tiebreak by lessonId. */
function byDueThenId(a: ReviewItem, b: ReviewItem): number {
  if (a.dueAt !== b.dueAt) {
    return a.dueAt - b.dueAt;
  }
  if (a.lessonId < b.lessonId) {
    return -1;
  }
  return a.lessonId > b.lessonId ? 1 : 0;
}

/** Minimal progress row the scheduler needs (subset of lesson_progress). */
export interface SrsProgressRow {
  lesson_id: string | null;
  course_id: string | null;
  status: string | null;
  score: number | null;
  attempts: number | null;
  completed_at: string | null;
  updated_at: string | null;
}

export interface ReviewItem {
  lessonId: string;
  courseId: string;
  /** Epoch ms when the review is due. */
  dueAt: number;
  /** Applied interval in days (for the UI). */
  intervalDays: number;
  /** true if already due (dueAt <= now). */
  due: boolean;
}

export interface ReviewQueue {
  /** Overdue, most overdue first. */
  due: ReviewItem[];
  /** Upcoming, soonest first. */
  upcoming: ReviewItem[];
}

/**
 * Interval (days) until the next review based on passes and the quality of
 * the last grade. `attempts` counts completed passes (>=1); odd values are clamped.
 */
export function computeReviewIntervalDays(attempts: number, score: number): number {
  const idx = Math.min(Math.max(Math.floor(attempts), 1), SRS_INTERVAL_LADDER_DAYS.length) - 1;
  const ladder = SRS_INTERVAL_LADDER_DAYS[idx] ?? 1;
  if (score >= SRS_STRONG_SCORE) {
    return ladder;
  }
  // Weak pass: comes back sooner (half interval, never less than 1 day).
  return Math.max(1, Math.round(ladder / 2));
}

/**
 * Timing of the next review for a progress row, or `null` if the row does
 * not enter the review cycle (not completed or no usable dates). The base is
 * the LAST touch (`updated_at`, falling back to `completed_at`): every
 * review played pushes the next one forward.
 */
export function computeNextReviewAt(row: SrsProgressRow): ReviewItem | null {
  if (row.status !== "completed" || !row.lesson_id || !row.course_id) {
    return null;
  }
  const baseIso = row.updated_at ?? row.completed_at;
  if (!baseIso) {
    return null;
  }
  const base = Date.parse(baseIso);
  if (Number.isNaN(base)) {
    return null;
  }
  const attempts = row.attempts ?? 1;
  const score = row.score ?? 0;
  const intervalDays = computeReviewIntervalDays(attempts, score);
  return {
    lessonId: row.lesson_id,
    courseId: row.course_id,
    dueAt: base + intervalDays * DAY_MS,
    intervalDays,
    due: false,
  };
}

/**
 * Review queue derived from synced progress: separates overdue from
 * upcoming and sorts deterministically (overdue: most overdue first;
 * upcoming: soonest first; ties broken by lessonId for stability).
 */
export function buildReviewQueue(rows: readonly SrsProgressRow[], now: number): ReviewQueue {
  const items: ReviewItem[] = [];
  for (const row of rows) {
    const item = computeNextReviewAt(row);
    if (item) {
      items.push({ ...item, due: item.dueAt <= now });
    }
  }
  return {
    due: items.filter((i) => i.due).toSorted(byDueThenId),
    upcoming: items.filter((i) => !i.due).toSorted(byDueThenId),
  };
}
